import type { QuietHours } from "@rhema/client";
import {
  Button,
  Chip,
  cn,
  focusRing,
  Select,
  SettingRow,
  SettingsGroup,
  Sheet,
  Switch,
  TextField,
} from "@rhema/ui";
import { useId, useState } from "react";
import { statusLine } from "../data/view";
import { clearChoices, whenLabel } from "../later/when";
import { useClient, useMe, useSession } from "../session/session";

const PRESETS = [
  { emoji: "📅", text: "In a meeting", clear: "1h" },
  { emoji: "🚌", text: "Commuting", clear: "30m" },
  { emoji: "🤒", text: "Out sick", clear: "today" },
  { emoji: "🌴", text: "On holiday", clear: "never" },
  { emoji: "🏠", text: "Working from home", clear: "today" },
  { emoji: "⛪", text: "At church", clear: "4h" },
];

/** Your status, which the people who see your about line see beside your name. */
export function StatusRow() {
  const me = useMe();
  const [open, setOpen] = useState(false);
  const line = statusLine(me);
  return (
    <SettingsGroup>
      <SettingRow
        title="Status"
        detail={
          line
            ? me.status_until
              ? `${line}. Clears ${whenLabel(me.status_until).toLowerCase()}.`
              : line
            : "Let people know you are busy or away."
        }
        trailing={
          <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
            {line ? "Change" : "Set a status"}
          </Button>
        }
      />
      {open && <StatusSheet open onClose={() => setOpen(false)} />}
    </SettingsGroup>
  );
}

/** The client keeps the signed-in person up to date. The session's copy follows it. */
function useSyncMe() {
  const client = useClient();
  const { setMe } = useSession();
  return () => {
    const next = client.getState().me;
    if (next) setMe(next);
  };
}

export function StatusSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const client = useClient();
  const me = useMe();
  const syncMe = useSyncMe();
  const set = Boolean(statusLine(me));
  const [emoji, setEmoji] = useState(set ? (me.status_emoji ?? "") : "");
  const [text, setText] = useState(set ? (me.status_text ?? "") : "");
  const [clear, setClear] = useState("today");
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const choices = clearChoices();

  const save = async () => {
    if (!emoji.trim() && !text.trim()) return setProblem("Write a status or pick an emoji.");
    setBusy(true);
    setProblem(null);
    try {
      await client.setStatus(emoji.trim(), text.trim(), choices.find((c) => c.id === clear)?.at ?? null);
      syncMe();
      onClose();
    } catch (e) {
      setProblem((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    setBusy(true);
    try {
      await client.clearStatus();
      syncMe();
      setEmoji("");
      setText("");
      onClose();
    } catch (e) {
      setProblem((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onClose={onClose} title="Set a status" className="pb-7">
      <ul aria-label="Suggestions" className="m-0 mt-3 flex list-none flex-wrap gap-2 p-0">
        {PRESETS.map((p) => (
          <li key={p.text}>
            <Chip
              size="sm"
              pressed={emoji === p.emoji && text === p.text}
              onClick={() => {
                setEmoji(p.emoji);
                setText(p.text);
                setClear(p.clear);
              }}
            >
              {p.emoji} {p.text}
            </Chip>
          </li>
        ))}
      </ul>
      <div className="mt-4 flex gap-2">
        <TextField
          label="Emoji"
          value={emoji}
          maxLength={8}
          onChange={(e) => setEmoji(e.target.value)}
          className="w-20 flex-none"
        />
        <TextField
          label="Status"
          value={text}
          maxLength={100}
          placeholder="What are you up to?"
          onChange={(e) => setText(e.target.value)}
          className="min-w-0 flex-1"
        />
      </div>
      <div className="mt-3">
        <Select
          label="Clear it"
          value={clear}
          onChange={setClear}
          options={choices.map((c) => ({ value: c.id, label: c.label }))}
        />
      </div>
      {problem && (
        <p role="alert" className="m-0 mt-3 text-sm text-scarlet">
          {problem}
        </p>
      )}
      <div className="mt-5 flex flex-col gap-2">
        <Button size="lg" disabled={busy} onClick={() => void save()}>
          Save the status
        </Button>
        {set && (
          <Button variant="ghost" disabled={busy} onClick={() => void remove()}>
            Clear the status
          </Button>
        )}
      </div>
    </Sheet>
  );
}

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function toTime(minutes: number): string {
  return `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;
}

function fromTime(value: string): number {
  const [h = "0", m = "0"] = value.split(":");
  return Number(h) * 60 + Number(m);
}

function localZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

/** Hours with no message notifications, such as nights and weekends. Calls still ring. */
export function QuietHoursSettings() {
  const client = useClient();
  const me = useMe();
  const syncMe = useSyncMe();
  const saved = me.quiet_hours ?? null;
  const startId = useId();
  const endId = useId();
  const [draft, setDraft] = useState<QuietHours>(
    () => saved ?? { start: 22 * 60, end: 7 * 60, tz: localZone(), days: 127, mentions: false },
  );
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const save = async (next: QuietHours | null) => {
    setBusy(true);
    setMessage(null);
    try {
      await client.setQuietHours(next ? { ...next, tz: localZone() } : null);
      syncMe();
      setMessage(null);
    } catch (e) {
      setMessage((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const changed =
    saved !== null &&
    (saved.start !== draft.start ||
      saved.end !== draft.end ||
      saved.days !== draft.days ||
      saved.mentions !== draft.mentions);

  return (
    <SettingsGroup>
      <SettingRow
        title="Quiet hours"
        detail={
          message ??
          (saved
            ? `No message notifications from ${toTime(saved.start)} to ${toTime(saved.end)}. Calls still ring.`
            : "Hold message notifications at night or at weekends. Calls still ring.")
        }
        trailing={
          <Switch
            label="Quiet hours"
            checked={saved !== null}
            disabled={busy}
            onChange={(on) => void save(on ? draft : null)}
          />
        }
      />
      {saved && (
        <div className="flex flex-col gap-3 px-4 py-3.5">
          <div className="flex gap-3">
            <label
              htmlFor={startId}
              className="flex flex-1 flex-col gap-1 text-[13px] font-semibold text-ink-secondary"
            >
              From
              <input
                id={startId}
                type="time"
                value={toTime(draft.start)}
                onChange={(e) => setDraft({ ...draft, start: fromTime(e.target.value) })}
                className="h-11 rounded-field border border-line bg-surface px-3 font-sans text-[15px] text-ink focus:border-blue focus:outline-none"
              />
            </label>
            <label
              htmlFor={endId}
              className="flex flex-1 flex-col gap-1 text-[13px] font-semibold text-ink-secondary"
            >
              To
              <input
                id={endId}
                type="time"
                value={toTime(draft.end)}
                onChange={(e) => setDraft({ ...draft, end: fromTime(e.target.value) })}
                className="h-11 rounded-field border border-line bg-surface px-3 font-sans text-[15px] text-ink focus:border-blue focus:outline-none"
              />
            </label>
          </div>
          <fieldset aria-label="Days" className="m-0 flex flex-wrap gap-1.5 border-0 p-0">
            {DAYS.map((day, i) => {
              const on = Boolean(draft.days & (1 << i));
              return (
                <button
                  key={day}
                  type="button"
                  aria-pressed={on}
                  onClick={() => {
                    const days = draft.days ^ (1 << i);
                    if (days) setDraft({ ...draft, days });
                  }}
                  className={cn(
                    "h-9 min-w-11 cursor-pointer rounded-full border px-3 text-sm font-semibold",
                    on
                      ? "border-blue bg-blue-tint text-blue-dark"
                      : "border-line bg-surface text-ink-secondary",
                    focusRing,
                  )}
                >
                  {day}
                </button>
              );
            })}
          </fieldset>
          <div className="flex items-center justify-between gap-3">
            <span className="text-[15px] font-semibold">Mentions still notify</span>
            <Switch
              label="Mentions still notify"
              checked={draft.mentions}
              onChange={(mentions) => setDraft({ ...draft, mentions })}
            />
          </div>
          {changed && (
            <Button size="sm" className="self-start" disabled={busy} onClick={() => void save(draft)}>
              Save quiet hours
            </Button>
          )}
        </div>
      )}
    </SettingsGroup>
  );
}
